/**
 * Phone number utility functions for formatting and validation
 */

/**
 * Formats a phone number to match backend validation requirements
 * Backend expects: ^\\+?[1-9]\\d{1,14}$ or ^[+]?91[6-9]\\d{9}$ for Indian numbers
 * @param {string} phoneNumber - Raw phone number input
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters except +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  // If it's an Indian number without country code, add +91
  if (/^[6-9]\d{9}$/.test(cleaned)) {
    return `+91${cleaned}`;
  }
  
  // If it's an Indian number with 91 prefix but no +, add +
  if (/^91[6-9]\d{9}$/.test(cleaned)) {
    return `+${cleaned}`;
  }
  
  // If it already has + and is valid, return as is
  if (/^\+[1-9]\d{1,14}$/.test(cleaned)) {
    return cleaned;
  }
  
  // If it's a number without + but starts with country code, add +
  if (/^[1-9]\d{1,14}$/.test(cleaned)) {
    return `+${cleaned}`;
  }
  
  // Return original if no formatting rules match
  return phoneNumber;
};

/**
 * Validates if a phone number matches backend requirements
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - True if valid
 */
export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  
  const formatted = formatPhoneNumber(phoneNumber);
  
  // Check against backend patterns
  const internationalPattern = /^\+?[1-9]\d{1,14}$/;
  const indianPattern = /^[+]?91[6-9]\d{9}$/;
  
  return internationalPattern.test(formatted) || indianPattern.test(formatted);
};

/**
 * Gets the phone number from user object with proper formatting
 * @param {object} user - User object
 * @returns {string} - Formatted phone number
 */
export const getUserPhoneNumber = (user) => {
  if (!user) return '';
  
  const rawPhone = user.phoneNumber || user.phone || '';
  return formatPhoneNumber(rawPhone);
};